import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareGlossarys = (s1, s2) => s1.id === s2.id

const glossarySlice = createSlice({
    name: 'glossary',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = {};
            action.payload.forEach(entry => {
                if (!state.data[entry.term]) {
                    state.data[entry.term] = [];
                }
                state.data[entry.term].push(entry);
            });
        },
        addOrUpdate: (state, action) => {
            for (const entry of ensureArray(action.payload)) {
                const oldTerm = Object.keys(state.data).find(term => state.data[term].some(e => compareGlossarys(entry, e)));
                if (oldTerm && oldTerm !== entry.term) {
                    let removeIdx = state.data[oldTerm].findIndex(e => compareGlossarys(entry, e));
                    if (removeIdx >= 0) {
                        state.data[oldTerm].splice(removeIdx, 1);
                    }
                }
                if (!state.data[entry.term]) {
                    state.data[entry.term] = [];
                }
                let idx = state.data[entry.term].findIndex(e => compareGlossarys(entry, e));
                if (idx >= 0) {
                    state.data[entry.term].splice(idx, 1, entry);
                } else {
                    state.data[entry.term].push(entry);
                }
            }
        },
        remove: (state, action) => {
            for (const entry of ensureArray(action.payload)) {
                const term = entry.term;
                if (state.data[term]) {
                    let idx = state.data[term].findIndex(e => compareGlossarys(e, entry));
                    if (idx >= 0) {
                        state.data[term].splice(idx, 1);
                        if (state.data[term].length === 0) {
                            delete state.data[term];
                        }
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => glossarySlice.getInitialState());
    }
});

// #region network operations
const fetchGlossaryTerms = network => network.get('/api/v1/glossary', glossarySlice.actions.set);
export const createGlossaryTerm = (network, term) => network.post('/api/v1/glossary', term, glossarySlice.actions.addOrUpdate);
export const updateGlossaryTerm = (network, term) => network.put('/api/v1/glossary', term, glossarySlice.actions.addOrUpdate);
export const removeGlossaryTerm = (network, term) => network.delete('/api/v1/glossary/' + encodeURIComponent(term.id), glossarySlice.actions.remove);
// #endregion

// #region permission checks
export const canUpdateGlossary = (credentials) => credentials.access.hasFull('administrator');
export const canCreateGlossary = (credentials) => credentials.access.hasFull('administrator');
export const canRemoveGlossary = (credentials) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectGlossaryTerms = state => state.glossary.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectGlossaryTermByName = createSelector([selectGlossaryTerms, (glossary, name) => name], (glossary, name) => glossary[name]);
const selectGlossary = name => state => selectGlossaryTermByName(state, name);
// #endregion

// #region listeners
const listen = {
    glossaryTermCreated: (glossary, { dispatch }) => dispatch(glossarySlice.actions.addOrUpdate(glossary)),
    glossaryTermUpdated: (glossary, { dispatch }) => dispatch(glossarySlice.actions.addOrUpdate(glossary)),
    glossaryTermRemoved: (glossary, { dispatch }) => dispatch(glossarySlice.actions.remove(glossary))
}
// #endregion

// #region custom hooks
export const [useGlossaryImpl,realizeGlossary] = dataHookFactory(selectGlossaryTerms, { run: fetchGlossaryTerms, listen });
export const [useGlossaryTerm] = dataHookFactory(selectGlossary, initMapFactory(name => fetchGlossaryTerms, listen));
// #endregion

const reducerDetails = { name: glossarySlice.name, reducer: glossarySlice.reducer };
export default reducerDetails;